import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";

/** Above MUI Dialog (theme zIndex.modal is typically 1300). */
const MODAL_PICKER_Z_INDEX = 1500;

/**
 * Ant Design date+time picker styled like orderflow ofTradeDatePicker,
 * for scheduling marketing messages (future date/time only).
 */
export default function ScheduleDateTimePicker({
    value,
    onChange,
    placeholder = "Select publish date and time",
    /** When true: no full-page backdrop; dropdown mounts inside the modal. */
    embeddedInModal = false,
}) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const disabledDate = (current) => {
        if (!current) return false;
        return current.isBefore(dayjs().startOf("day"));
    };

    const disabledTime = useMemo(
        () => (selectedDate) => {
            const selectedDayjs = dayjs(selectedDate);
            const now = dayjs();
            if (!selectedDayjs.isSame(now, "day")) {
                return {
                    disabledHours: () => [],
                    disabledMinutes: () => [],
                    disabledSeconds: () => [],
                };
            }
            const currentHour = now.hour();
            const currentMinute = now.minute();
            return {
                disabledHours: () =>
                    Array.from({ length: 24 }, (_, i) => i).filter(
                        (h) => h < currentHour,
                    ),
                disabledMinutes: (hour) => {
                    if (hour > currentHour) return [];
                    if (hour < currentHour) {
                        return Array.from({ length: 60 }, (_, i) => i);
                    }
                    return Array.from({ length: 60 }, (_, i) => i).filter(
                        (m) => m <= currentMinute,
                    );
                },
                disabledSeconds: () => [],
            };
        },
        [],
    );

    const backdropStyle = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 1049,
        display: open ? "block" : "none",
    };

    const getPopupContainer = embeddedInModal
        ? () => anchorRef.current || document.body
        : undefined;

    return (
        <div
            ref={anchorRef}
            style={{ position: "relative", width: "100%" }}
        >
            {!embeddedInModal &&
                open &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        style={backdropStyle}
                        onClick={() => setOpen(false)}
                    />,
                    document.body,
                )}
            <DatePicker
                allowClear
                open={open}
                onOpenChange={setOpen}
                format="DD-MM-YYYY HH:mm"
                showTime={{
                    format: "HH:mm",
                    use12Hours: false,
                    defaultValue: dayjs().add(1, "hour"),
                }}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
                placeholder={placeholder}
                onChange={(date) => onChange?.(date)}
                value={value}
                getPopupContainer={getPopupContainer}
                popupStyle={
                    embeddedInModal
                        ? { zIndex: MODAL_PICKER_Z_INDEX }
                        : undefined
                }
                style={{
                    border: "1px solid #415e81",
                    background: "#022D5A",
                    borderRadius: "4px",
                    width: "100%",
                    height: 40,
                }}
            />
        </div>
    );
}
